import { isDefined } from "@clipboard-health/util-ts";

import { WorkplaceNoteIdentifier } from "../constants";
import { type WorkplaceNotesRecord } from "../useWorkplaceNotesParsed";
import { WorkplaceHighlightType } from "./constants";
import {
  getWorkplaceHighlightLabel,
  type WorkplaceHighlightItem,
} from "./getWorkplaceHighlightLabel";
import type { WorkplaceHighlight } from "./types";

interface BuildWorkplacePolicyHighlightsProps {
  isExtraTimePayEnabled: boolean;
  lateCancellationPeriod: number;
  lateCancellationFeeHours: number;
  requiresLunchBreak?: boolean;
  providesRestBreaks?: boolean;
  workplaceNotes: WorkplaceNotesRecord;
  mandatoryBreakDurationInMinutes: number;
  isIndependentContractorBadgesRequired: boolean;
}

export const buildWorkplacePolicyHighlights = ({
  isExtraTimePayEnabled,
  lateCancellationPeriod,
  lateCancellationFeeHours,
  requiresLunchBreak,
  providesRestBreaks,
  workplaceNotes,
  mandatoryBreakDurationInMinutes,
  isIndependentContractorBadgesRequired,
}: BuildWorkplacePolicyHighlightsProps): WorkplaceHighlight[] => {
  const highlightItems: WorkplaceHighlightItem[] = [];

  if (isExtraTimePayEnabled) {
    highlightItems.push({
      type: WorkplaceHighlightType.EXTRA_TIME_PAY,
    });
  }

  const offersCancellationPayout = lateCancellationPeriod > 0 && lateCancellationFeeHours > 0;

  if (offersCancellationPayout) {
    highlightItems.push({
      type: WorkplaceHighlightType.CANCELLATION_PAYOUT,
      value: lateCancellationPeriod,
    });
  }

  const hasNote = (identifier: WorkplaceNoteIdentifier): boolean => {
    return isDefined(workplaceNotes[identifier]);
  };

  const hasBreaks =
    (requiresLunchBreak === true && hasNote(WorkplaceNoteIdentifier.MANDATORY_BREAK_POLICY)) ||
    (providesRestBreaks === true && hasNote(WorkplaceNoteIdentifier.REST_BREAK_POLICY));

  if (hasBreaks) {
    highlightItems.push({
      type: WorkplaceHighlightType.BREAKS,
    });
  }

  if (hasNote(WorkplaceNoteIdentifier.DRESS_CODE_INSTRUCTIONS)) {
    highlightItems.push({
      type: WorkplaceHighlightType.DRESS_CODE,
    });
  }

  if (isIndependentContractorBadgesRequired) {
    highlightItems.push({
      type: WorkplaceHighlightType.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED,
    });
  }

  if (hasNote(WorkplaceNoteIdentifier.LATE_CLOCK_OUT_POLICY)) {
    highlightItems.push({
      type: WorkplaceHighlightType.LATE_CLOCK_OUT,
    });
  }

  return highlightItems.map((highlightItem) => ({
    type: highlightItem.type,
    label: getWorkplaceHighlightLabel(highlightItem, mandatoryBreakDurationInMinutes),
  }));
};
